<section class="w-full">
  <p class="text-4xl font-normal mt-[0.9rem]">Schedule a Shift</p>

  <form [formGroup]="checkInForm" (ngSubmit)="checkIn()">
    <div class="flex mt-5">
      <div class="w-full ">
        <!-- Client Type - first field; drives the rest of the form. -->
        <tui-combo-box [stringify]="stringifyClientType" formControlName="clientType" class="mb-3">
          Select Client Type
          <input tuiTextfieldLegacy placeholder="Select Client Type" />
          <tui-data-list-wrapper *tuiDataList [items]="clientTypeOptions"
            [itemContent]="stringifyClientType | tuiStringifyContent" />
        </tui-combo-box>
        <tui-error formControlName="clientType" class="mb-3" [error]="[] | tuiFieldError | async" />

        <!-- Select Client - filtered by the chosen Client Type. -->
        <div class="mb-3 relative min-h-[56px] w-full">
          <div *ngIf="isClientsLoading"
            class="w-[150] p-4 border border-dashed border-gray-300 rounded-lg bg-gray-50 flex items-center gap-3 animate-pulse">
            <div class="w-4 h-4 rounded-full border-2 border-t-transparent border-indigo-600 animate-spin"></div>
            <span class="text-sm text-gray-500 font-medium">Updating available clients...</span>
          </div>
          <div *ngIf="!isClientsLoading">
            <tui-combo-box formControlName="client" [tuiTextfieldLabelOutside]="true" [stringify]="stringifyClients"
              placeholder="Select Client" [(search)]="clientSearch" [strictMatcher]="null">
              Select Client
              <tui-data-list-wrapper *tuiDataList [items]="filteredClients"
                [itemContent]="stringifyClients | tuiStringifyContent">
              </tui-data-list-wrapper>
            </tui-combo-box>
            <label class="flex items-center gap-2 text-xs text-[#191C30E5] select-none mt-1">
              <input type="checkbox" [checked]="includeInactiveClients"
                (change)="toggleIncludeInactiveClients(!includeInactiveClients)" />
              Include inactive / archived clients
            </label>
            <p *ngIf="isSelectedClientInactive()" class="text-xs text-[#8a6d1a] mt-1 flex items-center gap-1">
              <span
                class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#fde68a] text-[#92400e] text-[10px] font-bold">!</span>
              This client is archived or inactive; saving will use historical data only.
            </p>
          </div>
        </div>
        <tui-error class="mb-3" [error]="[] | tuiFieldError | async" />

        <!-- Select Nurse - all client types. -->
        <tui-combo-box formControlName="nurse" [tuiTextfieldLabelOutside]="true" [stringify]="stringifyNurses"
          placeholder="Select Nurse" [(search)]="nurseSearch" [strictMatcher]="null">
          Select Nurse
          <tui-data-list-wrapper *tuiDataList [items]="filteredNurses"
            [itemContent]="stringifyNurses | tuiStringifyContent">
          </tui-data-list-wrapper>
        </tui-combo-box>
        <tui-error class="mb-3" formControlName="nurse" [error]="[] | tuiFieldError | async" />

        <!-- Select Type - only for Registered + Unregistered. -->
        <ng-container *ngIf="!isInwardClient">
          <tui-combo-box [stringify]="stringifyDropdown" formControlName="typeOption" class="mb-3">
            Select Type
            <input tuiTextfieldLegacy placeholder="Select Type" />
            <tui-data-list-wrapper *tuiDataList [items]="typeOptions"
              [itemContent]="stringifyDropdown | tuiStringifyContent" />
          </tui-combo-box>
          <tui-error formControlName="typeOption" class="mb-3" [error]="[] | tuiFieldError | async" />
        </ng-container>

        <!-- Select Roster - all client types. -->
        <tui-combo-box [stringify]="stringifyDropdown" formControlName="roster">
          Select Roster
          <input tuiTextfieldLegacy placeholder="Select Roster" />
          <tui-data-list-wrapper *tuiDataList [items]="rosterItems"
            [itemContent]="stringifyDropdown | tuiStringifyContent" />
        </tui-combo-box>
        <tui-error formControlName="roster" class="mb-3" [error]="[] | tuiFieldError | async" />

        <div *ngIf="checkInForm.get('roster')?.value?.value === 12">
          <tui-combo-box [stringify]="stringifyDropdown" formControlName="rosterType">
            Select Roster Type
            <input tuiTextfieldLegacy placeholder="Select Roster type" />
            <tui-data-list-wrapper *tuiDataList [items]="rosterType"
              [itemContent]="stringifyDropdown | tuiStringifyContent" />
          </tui-combo-box>
          <tui-error formControlName="rosterType" class="mb-3" [error]="[] | tuiFieldError | async" />
        </div>

        <!-- Select Meal Option - all client types. -->
        <tui-combo-box [stringify]="stringifyDropdown" formControlName="mealOption">
          Select Meal Option
          <input tuiTextfieldLegacy placeholder="Select Meal Option" />
          <tui-data-list-wrapper *tuiDataList [items]="mealOptions"
            [itemContent]="stringifyDropdown | tuiStringifyContent" />
        </tui-combo-box>
        <tui-error formControlName="mealOption" class="mb-3" [error]="[] | tuiFieldError | async" />

        <!-- Address - only Registered + Unregistered (optional). -->
        <ng-container *ngIf="!isInwardClient">
          <div class="w-full">
            <label tuiLabel>
              <tui-textarea formControlName="address" [tuiTextfieldLabelOutside]="true"
                placeholder="Enter Address (optional)" title="Textarea Input">
                Address
              </tui-textarea>
              <tui-error formControlName="address" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>
          </div>
        </ng-container>

        <!-- Room + BHT Number are Inward-only operational identifiers
             (pre-filled from the client doc, still editable). They are
             intentionally not surfaced for Registered / Non Registered
             shifts, including NHP (National Hospital). -->
        <div *ngIf="isInwardClient">
          <div class="w-full">
            <label tuiLabel>
              <tui-input formControlName="room">
                Room
                <input tuiTextfieldLegacy type="text" />
              </tui-input>
              <tui-error formControlName="room" class="mb-3" [error]="[] | tuiFieldError | async"></tui-error>
            </label>
          </div>

          <div class="w-full">
            <label tuiLabel>
              <tui-input formControlName="bhtNumber">
                BHT Number
                <input tuiTextfieldLegacy type="text" />
              </tui-input>
              <tui-error formControlName="bhtNumber" class="mb-3" [error]="[] | tuiFieldError | async"></tui-error>
            </label>
          </div>
        </div>

        <!-- Select Date or Date Range -->
        <div>
          <tui-input-date-range formControlName="startDateRange" [tuiTextfieldLabelOutside]="true"
            placeholder="Select Date or Date Range">
            Select Date or Date Range
          </tui-input-date-range>
          <tui-error formControlName="startDateRange" class="mb-3" [error]="[] | tuiFieldError | async"></tui-error>
        </div>

        <!-- Additional Notes - only Registered + Unregistered (optional). -->
        <ng-container *ngIf="!isInwardClient">
          <div class="w-full">
            <label tuiLabel>
              <tui-textarea formControlName="notes" [tuiTextfieldLabelOutside]="true"
                placeholder="Enter notes (optional)" title="Textarea Input">
                Additional Notes
              </tui-textarea>
              <tui-error formControlName="notes" class="mb-3" [error]="[] | tuiFieldError | async" />
            </label>
          </div>
        </ng-container>

        <!-- In-Form Scheduling Error Alert Box -->
        <div *ngIf="errorMessage"
          class="mt-3 p-3.5 rounded-lg bg-[#fef2f2] border border-[#fecaca] text-[#991b1b] text-xs flex items-start gap-2.5 shadow-sm">
          <div class="flex-1 font-semibold leading-relaxed">{{ errorMessage }}</div>
        </div>

        <div class="mt-5 space-x-5 flex justify-end">
          <!-- Cancel Button -->
          <button appearance="outline" tuiButton type="button" (click)="cancelCheckin()">
            Cancel
          </button>

          <!-- Submit Button -->
          <button appearance="primary" tuiButton type="submit" class="w-[200px]" [disabled]="isLoading"
            [appDisableUnlessPermission]="'SHIFT_CREATE'">
            <!-- State: Not Loading -->
            <ng-container *ngIf="!isLoading">
              Schedule Shift(s)
            </ng-container>

            <!-- State: Loading -->
            <ng-container *ngIf="isLoading">
              Scheduling...
            </ng-container>
          </button>
        </div>
      </div>
    </div>
  </form>
</section>